import { Box, Divider, makeStyles } from '@material-ui/core';
import { FilterOperator } from '@nemuru-eng/js-api-client';

import { ListFilter } from '@/components/modules/CheckoutSession/List/types/ListFilter';
import { ALLOWED_CREDIT_CARD_STATUSES_FOR_MULTI_SELECT_FILTER } from '@/components/modules/CreditCard/List/constants/creditCardStatusConfig';
import { ALLOWED_MERCHANT_STATUSES_FOR_MULTI_SELECT_FILTER } from '@/components/modules/MerchantsList/constants/constants';
import { ALLOWED_TRANSACTION_TYPES_FOR_MULTI_SELECT_FILTER } from '@/components/modules/Payouts/PayoutDetails/TransactionsList/constants/constants';
import { ALLOWED_PAYOUT_STATUSES_FOR_MULTI_SELECT_FILTER } from '@/components/modules/Payouts/PayoutsList/constants/constants';
import { CustomGridColDef, ListPayload } from '@/components/shared/components/FetchingDataGrid/FetchingDataGrid';
import { LiveUpdatesProps } from '@/components/shared/components/FetchingDataGrid/hooks/useLiveList';
import { FiltersDropdownTool } from '@/components/shared/components/FiltersDropdownTool/FiltersDropdownTool';
import { Font } from '@/components/shared/components/Font';
import { StandardList } from '@/components/shared/components/StandardList';
import { Text } from '@/components/shared/components/Text';
import { ALLOWED_CHECKOUT_SESSION_STATUSES_FOR_MULTI_SELECT_FILTER } from '@/config/checkoutSessionConfig';
import { ALLOWED_LOAN_STATUSES_FOR_MULTI_SELECT_FILTER } from '@/config/loanConfig';
import { ALLOWED_ORDER_STATUSES_FOR_MULTI_SELECT_FILTER } from '@/config/orderConfig';
import { LoanListByProductConfig } from '@/config/types';
import { BooleanFilterDefinition, useBooleanFilters } from '@/hooks/useBooleanFilters';
import { useComponentsConfig } from '@/hooks/useComponentsConfig';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useDateFilters } from '@/hooks/useDateFilters';
import { useGetCurrentPathId } from '@/hooks/useGetCurrentPathId';
import { useMultiSelectFilters } from '@/hooks/useMultiSelectFilters';
import { ListQueryProps } from '@/types/ListQueryProps';

interface ConfigFromQuery {
  query: { filters: ListFilter[] };
  filterDateValues?: { fromDate?: Date; toDate?: Date };
}
interface MultiFilteredListProps {
  fetchList: (query: ListQueryProps) => Promise<ListPayload>;
  dataGridFields: CustomGridColDef[];
  onRowClick: (row) => void;
  getXLSXExport?: (query: ListQueryProps) => Promise<string>;
  multiselectProps?: Parameters<typeof useMultiSelectFilters>[0];
  dateFilterProps?: { dateField: string };
  booleanFiltersProps?: BooleanFilterDefinition[];
  defaultOrderBy?: string;
  defaultOrderDirection?: 'asc' | 'desc';
  showExport?: boolean;
  getRowClassName?: (params) => string;
  loading?: boolean;
  disableSelectionOnClick?: boolean;
  showSearch?: boolean;
  liveUpdates?: LiveUpdatesProps;
}

export const MultiFilteredList = ({
  fetchList,
  getXLSXExport,
  dataGridFields,
  onRowClick,
  multiselectProps,
  dateFilterProps,
  booleanFiltersProps,
  defaultOrderBy,
  defaultOrderDirection = 'desc',
  showExport,
  getRowClassName,
  loading,
  disableSelectionOnClick,
  showSearch,
  liveUpdates,
  ...props
}: MultiFilteredListProps) => {
  const { setComponentsConfig, getComponentConfigById } = useComponentsConfig();

  const componentId = `${useGetCurrentPathId()}${'DEAL_LIST'}`;
  const multiSelectField = multiselectProps?.field;
  const config = getComponentConfigById(componentId) as LoanListByProductConfig;

  const { palette } = useCustomTheme();

  const getFiltersConfigFromQuery = (config: ConfigFromQuery) => {
    const query = config?.query;
    const dateValues = config?.filterDateValues;

    const ALLOWED_STATUSES_FOR_MULTI_SELECT_FILTER = [
      ...ALLOWED_LOAN_STATUSES_FOR_MULTI_SELECT_FILTER,
      ...ALLOWED_ORDER_STATUSES_FOR_MULTI_SELECT_FILTER,
      ...ALLOWED_MERCHANT_STATUSES_FOR_MULTI_SELECT_FILTER,
      ...ALLOWED_PAYOUT_STATUSES_FOR_MULTI_SELECT_FILTER,
      ...ALLOWED_TRANSACTION_TYPES_FOR_MULTI_SELECT_FILTER,
      ...ALLOWED_CHECKOUT_SESSION_STATUSES_FOR_MULTI_SELECT_FILTER,
      ...ALLOWED_CREDIT_CARD_STATUSES_FOR_MULTI_SELECT_FILTER,
    ];
    const fromDate = dateValues?.fromDate;
    const toDate = dateValues?.toDate;
    const selectedStatuses = query?.filters
      .find((e) => e.field === multiSelectField && e.operator === FilterOperator.IN)
      ?.value?.filter((status) => (ALLOWED_STATUSES_FOR_MULTI_SELECT_FILTER as unknown[]).includes(status) === true);

    return { toDate, fromDate, selectedStatuses };
  };
  const { selectedStatuses, toDate, fromDate } = getFiltersConfigFromQuery(config);

  const { dateFilters, dateFiltersContent, fromDateValue, toDateValue, isDefaultDateValue, resetDateFilters } =
    useDateFilters({
      defaultValues: { fromDate, toDate },
      dateField: dateFilterProps?.dateField,
      showSearch,
    });

  const { multiSelectFilters, multiSelectFiltersContent, isDefaultMultiSelectValue, resetMultiSelectToDefault } =
    useMultiSelectFilters({
      ...multiselectProps,
      initialItems: selectedStatuses,
      hideLabel: true,
    });

  const {
    booleanFilters,
    booleanFiltersContent,
    activeIds: activeBooleanFilterIds,
    nonDefaultBooleanFilterCount,
    resetBooleanFiltersToDefaults,
  } = useBooleanFilters({
    definitions: booleanFiltersProps ?? [],
    initialActiveIds: config?.activeBooleanFilterIds,
  });

  const defaultPage = config?.query?.pageNumber || 0;
  const defaultSearchString = config?.query?.searchString || '';
  const defaultPageSize = config?.query?.pageSize || '';
  const orderBy = config?.query?.orderBy || defaultOrderBy;
  const direction = config?.query?.orderDirection || defaultOrderDirection;

  const handleFetch = (query: ListQueryProps) => {
    setConfigByQuery(query);
    return fetchList(query);
  };

  const setConfigByQuery = (query: ListQueryProps) => {
    const { pageNumber, searchString, pageSize, orderBy, orderDirection, filters } = query;
    const config = {
      id: componentId,
      query: { pageNumber, searchString, pageSize, orderBy, orderDirection, filters },
      filterDateValues: { fromDate: fromDateValue, toDate: toDateValue },
      activeBooleanFilterIds,
    };
    setComponentsConfig([config]);
  };

  const sortedFieldIndex = dataGridFields.findIndex((field) => field.field === orderBy);
  const sortingIsNotDefault =
    sortedFieldIndex !== -1 && (orderBy !== defaultOrderBy || direction !== defaultOrderDirection);

  const useStyles = makeStyles({
    root: {
      '& .MuiDataGrid-columnHeader--sorted .MuiButtonBase-root': {
        backgroundColor: sortingIsNotDefault ? palette.grey[200] : undefined,
      },
      '& .MuiDataGrid-cell': {
        opacity: loading ? '0.5 !important' : 1,
      },
    },
  });

  const customClasses = useStyles();

  const showOtherFiltersSection = Boolean(booleanFiltersProps?.length);
  const shouldRenderFiltersTool = Boolean(multiselectProps || dateFilterProps || showOtherFiltersSection);
  const activeFiltersDeviationCount =
    (!isDefaultMultiSelectValue && Boolean(multiselectProps) ? 1 : 0) +
    (!isDefaultDateValue && Boolean(dateFilterProps) ? 1 : 0) +
    (showOtherFiltersSection ? nonDefaultBooleanFilterCount : 0);
  const hasActiveFilters = activeFiltersDeviationCount > 0;

  const handleResetAllFilters = () => {
    if (multiselectProps) {
      resetMultiSelectToDefault();
    }
    if (dateFilterProps) {
      resetDateFilters();
    }
    if (showOtherFiltersSection) {
      resetBooleanFiltersToDefaults();
    }
  };

  return (
    <StandardList
      dataGridFields={dataGridFields as CustomGridColDef[]}
      fetchData={(query) => handleFetch(query)}
      getXLSXExport={getXLSXExport}
      onCellClick={(selectedCell) => {
        selectedCell.colDef.disableOnRowClick ? null : onRowClick(selectedCell);
      }}
      classes={customClasses}
      style={{ padding: 0 }}
      filters={[...dateFilters, ...multiSelectFilters, ...booleanFilters]}
      defaultPageNumber={defaultPage}
      defaultSearchString={defaultSearchString}
      defaultPageSize={defaultPageSize}
      defaultOrderBy={orderBy}
      defaultOrderDirection={direction}
      showExport={showExport}
      getRowClassName={getRowClassName}
      loading={loading}
      disableSelectionOnClick={disableSelectionOnClick}
      booleanFilterTools={
        shouldRenderFiltersTool ? (
          <FiltersDropdownTool
            paperWidth={420}
            hasActiveFilters={hasActiveFilters}
            activeFiltersDeviationCount={activeFiltersDeviationCount}
            onResetFilters={handleResetAllFilters}
          >
            <Box px={2} pb={1}>
              <Font variant="caption" color="textSecondary">
                <Text tId="DATE" />
              </Font>
            </Box>
            <Box px={2} pt={'4px'}>
              {dateFiltersContent}
            </Box>

            <Divider />

            <Box px={2} pt={2} pb={1}>
              <Font variant="caption" color="textSecondary">
                <Text tId="STATE" />
              </Font>
            </Box>
            <Box px={2} pb={1}>
              {multiSelectFiltersContent}
            </Box>

            {showOtherFiltersSection && (
              <>
                <Divider />
                <Box px={2} pt={2} pb={1}>
                  <Font variant="caption" color="textSecondary">
                    <Text tId="OTHER_FILTERS" />
                  </Font>
                </Box>
                <Box pl={1} pr={2} pb={1}>
                  {booleanFiltersContent}
                </Box>
              </>
            )}
          </FiltersDropdownTool>
        ) : undefined
      }
      showSearch={showSearch}
      liveUpdates={liveUpdates}
      {...props}
    />
  );
};
